import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { waLink } from '../../lib/wa.js';
import { buildRequiredForRole, listMissingMandatory } from '@cls/shared';

interface Cert { id: string; type: string; expiry: string | null; status: 'valid' | 'expiring' | 'expired'; }
interface Worker { id: string; name: string; phone: string; email: string | null; role: string; roles?: string[]; certs: Cert[]; }
interface RoleRequirement { role: string; mandatory: string[]; }

interface Props { onClose: () => void; workerId?: string; mode?: 'expiry' | 'missing'; }

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}
function avEl(name: string, size = 30, font = 11): JSX.Element {
  return (
    <div style={{ width: size, height: size, minWidth: size, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: font, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#0B0F1A', flexShrink: 0 }}>
      {initials(name)}
    </div>
  );
}
function cBdg(status: 'valid' | 'expiring' | 'expired' | 'missing'): JSX.Element {
  const map = {
    valid: { bg: 'rgba(34,197,94,.15)', fg: '#22C55E', lbl: 'Valid' },
    expiring: { bg: 'rgba(245,158,11,.18)', fg: '#F59E0B', lbl: 'Expiring' },
    expired: { bg: 'rgba(239,68,68,.18)', fg: '#EF4444', lbl: 'Expired' },
    missing: { bg: 'rgba(239,68,68,.18)', fg: '#EF4444', lbl: 'Missing' },
  }[status];
  return <span style={{ background: map.bg, color: map.fg, borderRadius: 999, padding: '1px 7px', fontSize: 9, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>{map.lbl}</span>;
}

export function CertNotifyModal({ onClose, workerId, mode = 'expiry' }: Props) {
  const [useWa, setUseWa] = useState(true);
  const [useEmail, setUseEmail] = useState(false);
  const [useSms, setUseSms] = useState(false);
  const missing = mode === 'missing';

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: roleReqs = [] } = useQuery({
    queryKey: ['training', 'role-requirements'],
    queryFn: () => api.get<RoleRequirement[]>('/training/role-requirements').then((r) => r.data),
    enabled: missing,
  });

  const grouped = useMemo(() => {
    const target = workerId ? workers.filter((w) => w.id === workerId) : workers;
    if (missing) {
      const requiredForRole = buildRequiredForRole(roleReqs);
      return target
        .map((w) => ({ worker: w, certs: [] as Cert[], missing: listMissingMandatory(w, requiredForRole) }))
        .filter((g) => g.missing.length > 0);
    }
    return target
      .map((w) => ({ worker: w, certs: w.certs.filter((c) => c.status !== 'valid'), missing: [] as string[] }))
      .filter((g) => g.certs.length > 0);
  }, [workers, workerId, missing, roleReqs]);

  const totalItems = grouped.reduce((n, g) => n + g.certs.length + g.missing.length, 0);
  const subject = missing ? 'Mandatory Training Required — CLS' : 'Certificate Renewal Required — CLS';

  const send = useMutation({
    mutationFn: async () => {
      const methods: string[] = [];
      if (useWa) methods.push('whatsapp');
      if (useEmail) methods.push('email');
      if (useSms) methods.push('sms');
      const result = await api.post('/broadcasts', {
        messageBody: missing
          ? `Missing mandatory training reminder — ${totalItems} certificate(s) required.`
          : `Certificate renewal reminder — ${totalItems} cert(s) need attention.`,
        subject,
        channels: methods,
        recipientIds: grouped.map((g) => g.worker.id),
        audienceSummary: missing
          ? `${grouped.length} workers missing mandatory training`
          : `${grouped.length} workers with cert alerts`,
      });
      return { result, methods };
    },
    onSuccess: ({ methods }) => {
      for (const g of grouped) {
        const msg = missing ? buildMissingMsg(g.worker.name, g.missing) : buildMsg(g.worker.name, g.certs);
        if (methods.includes('whatsapp') && g.worker.phone) {
          window.open(waLink(g.worker.phone, msg), '_blank', 'noopener,noreferrer');
        }
        if (methods.includes('email') && g.worker.email) {
          window.open(`mailto:${g.worker.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
        }
        if (methods.includes('sms') && g.worker.phone) {
          window.open(`sms:${g.worker.phone}?body=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
        }
      }
      toast.success('Notifications sent', `${grouped.length} worker${grouped.length !== 1 ? 's' : ''} via ${methods.length} channel${methods.length !== 1 ? 's' : ''}`);
      onClose();
    },
    onError: () => toast.error('Send failed', 'Try again'),
  });

  const buildMsg = (name: string, certs: Cert[]) =>
    `Hi ${name.split(' ')[0]},\n\nThis is a CLS reminder that the following certificate${certs.length > 1 ? 's' : ''} require${certs.length === 1 ? 's' : ''} URGENT renewal:\n\n${certs.map((c) => `- ${c.type} — ${c.status === 'expired' ? 'EXPIRED' : 'EXPIRING'} ${c.expiry?.slice(0, 10) ?? ''}`).join('\n')}\n\nPlease complete the above as soon as possible and if you have any additional certificates please WhatsApp them through to us, thank you.\n\nConstruction Logistics Services Ltd`;

  const buildMissingMsg = (name: string, certs: string[]) =>
    `Hi ${name.split(' ')[0]},\n\nThis is CLS — our records show the following mandatory training ${certs.length > 1 ? 'courses are' : 'course is'} missing for your role:\n\n${certs.map((c) => `- ${c}`).join('\n')}\n\nTo complete your training please go to www.cl-ac.uk and if you already hold ${certs.length > 1 ? 'any of these certificates' : 'this certificate'} please WhatsApp ${certs.length > 1 ? 'them' : 'it'} through to us, thank you.\n\nConstruction Logistics Services Ltd`;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 640, maxWidth: '98vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{missing ? '🔔 Missing Training Notifications' : '🔔 Certificate Expiry Notifications'}</div>
        <div className="modal-sub" id="cn-sub">
          {missing
            ? `${totalItems} missing mandatory certificate${totalItems !== 1 ? 's' : ''} across ${grouped.length} worker${grouped.length !== 1 ? 's' : ''}`
            : `${totalItems} certificate alert${totalItems !== 1 ? 's' : ''} found across ${grouped.length} worker${grouped.length !== 1 ? 's' : ''}`}
        </div>
        <div id="cn-list" style={{ maxHeight: 400, overflowY: 'auto' }}>
          {grouped.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
              {missing ? 'No missing mandatory training to notify.' : 'No cert alerts to notify.'}
            </div>
          ) : grouped.map((g) => (
            <div key={g.worker.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 9 }}>
              <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 9 }}>
                {avEl(g.worker.name)}
                <div>
                  <div style={{ fontWeight: 500, fontSize: 12 }}>{g.worker.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>{g.worker.role} · {g.worker.phone}</div>
                </div>
              </div>
              {g.certs.map((c) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 0', borderTop: '1px solid rgba(30,42,64,.4)', fontSize: 11 }}>
                  {cBdg(c.status)}
                  <span style={{ flex: 1 }}>{c.type}</span>
                  <span style={{ color: 'var(--muted)' }}>{c.expiry?.slice(0, 10) ?? '—'}</span>
                </div>
              ))}
              {g.missing.map((m) => (
                <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 0', borderTop: '1px solid rgba(30,42,64,.4)', fontSize: 11 }}>
                  {cBdg('missing')}
                  <span style={{ flex: 1 }}>{m}</span>
                  <span style={{ color: 'var(--muted)' }}>required for role</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="form-row" style={{ marginTop: 14 }}>
          <label className="form-label">Notification Method</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 5 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="checkbox" id="cn-wa" checked={useWa} onChange={(e) => setUseWa(e.target.checked)} style={{ accentColor: '#25D366' }} /> WhatsApp</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="checkbox" id="cn-email" checked={useEmail} onChange={(e) => setUseEmail(e.target.checked)} style={{ accentColor: 'var(--info)' }} /> Email</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}><input type="checkbox" id="cn-sms" checked={useSms} onChange={(e) => setUseSms(e.target.checked)} style={{ accentColor: 'var(--accent)' }} /> SMS</label>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-warn" onClick={() => send.mutate()} disabled={send.isPending || (!useWa && !useEmail && !useSms) || grouped.length === 0}>
            {send.isPending ? 'Sending…' : '🔔 Send Notifications'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
